import { Guest } from "@/types/Guest";
import { GuestState } from "@/types/enums/GuestState";
import { GuestType } from "@/types/enums/GuestType";

/**
 * The name every placeholder companion carries until somebody renames it.
 *
 * NOT translated, on purpose. It is a marker the operator scans a list for,
 * and it has to stay the same string for the guest whose party was entered by
 * a German colleague and renamed by an English one — a locale-dependent name
 * would also mean the same party reads as two different kinds of row. It is
 * also what {@link nextPlaceholderNumber} matches on to keep numbering.
 */
export const PLACEHOLDER_FIRST_NAME = "TBD";

const isPlaceholder = (guest: Guest) =>
  (guest.firstName ?? "").trim().toUpperCase() === PLACEHOLDER_FIRST_NAME;

/**
 * The number the next placeholder should carry, so a second "+3" continues at
 * 4 instead of minting a second "TBD 1".
 *
 * Reads the host's CURRENT companion list — which is what the guest-detail
 * popup has on screen, and which may be stale by one concurrent create from
 * another operator. A collision costs nothing (the rows are placeholders and
 * carry no unique key), so this deliberately does not pay for a refetch.
 */
export function nextPlaceholderNumber(existing: Guest[] | null | undefined): number {
  let highest = 0;
  for (const guest of existing ?? []) {
    if (!isPlaceholder(guest)) continue;
    const suffix = (guest.lastName ?? "").trim();
    // Digits only, and few enough of them to still be a row number: a renamed
    // "TBD Müller" is not part of the sequence (`parseInt` would read a leading
    // number out of "12 Müller" and `Number("")` is 0, both of which would put
    // a non-placeholder into the count), and an imported 30-digit "number"
    // would push `start` past 2^53 — `String(1e21 + 1)` is the literal
    // "1e+21", which is not a row number either.
    if (!/^\d{1,6}$/.test(suffix)) continue;
    const parsed = Number.parseInt(suffix, 10);
    if (parsed > highest) highest = parsed;
  }
  return highest + 1;
}

/**
 * `count` companions of `mainGuestId`, named "TBD 1", "TBD 2", … continuing
 * after the placeholders that host already has.
 *
 * `state` mirrors what the create FORM seeds (UNCONTACTED) rather than what
 * the door happens to default to — these rows are the same kind of hand-added
 * guest, just without the typing. `acceptanceState` is deliberately absent:
 * the create door writes SYSTEM itself for every row, and inventing one here
 * is exactly the mistake `performCreate`'s comment warns about.
 *
 * `additionalFields` is absent too, and that is a DELIBERATE divergence from
 * the full form, which always sends `{}`. The backend's gate skips a submission
 * that is null (`GuestAdditionalFieldGate`: `if (sub == null) continue`), so an
 * event with a REQUIRED custom field still accepts placeholders. Sending `{}`
 * would make that event refuse the whole batch — and a row whose entire point
 * is that nobody knows anything about the person yet cannot satisfy a required
 * field. The value is filled in when the row is renamed, through the ordinary
 * edit path, which does enforce it.
 */
export function buildPlaceholderCompanions(
  count: number,
  mainGuestId: string,
  existing: Guest[] | null | undefined,
): Guest[] {
  const start = nextPlaceholderNumber(existing);
  return Array.from({ length: count }, (_, index) => ({
    guestType: GuestType.COMPANION,
    mainGuest: mainGuestId,
    firstName: PLACEHOLDER_FIRST_NAME,
    lastName: String(start + index),
    state: GuestState.UNCONTACTED,
  }));
}
